import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import AddIcon from "../Assets/svgs/AddIcon";
import CloseIcon from "../Assets/svgs/CloseIcon";
import CollapseIcon from "../Assets/svgs/CollapseIcon";
import DeleteIcon from "../Assets/svgs/DeleteIcon";
import { Loader } from "../Common/Loader";
import { uuidGenerator } from "../Common/utils";
import { validURL } from "../Common/Validations";
import { addFunction } from "../Redux/Slice/Functions/AddFunctionSlice";
import { ErrorModel } from "./ErrorModel";

const CreateFunctionModal = ({ closePopup, onSuccess }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    serverUrl: "",
    serverUrlToken: "",
    timeoutSeconds: "",
    headers: [],
    schema: [],
    name: "",
    description: "",
    startMessage: "",
    delayedResponseMessage: "",
    successMessage: "",
    errorMessage: "",
  });
  const [formError, setFormError] = useState({});
  const [step, setStep] = useState(1);
  const [currentSchema, setCurrentSchema] = useState(null);
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });

  const handleChange = async (e) => {
    let { name, value } = e.target;
    const errorKey = `${name}Error`;
    value = value.trimStart();
    if (name === "serverUrl") {
      const isValid = validURL(value);
      if (isValid || value.trim() === "") {
        setFormError({ ...formError, [errorKey]: "" });
      } else {
        setFormError({
          ...formError,
          [errorKey]: "Please enter valid Server Url",
        });
      }
    } else if (name == "timeoutSeconds") {
      value = value.replace(/[^0-9]/g, "");
      if ((value > 1 && value < 120) || !value) {
        setFormError({ ...formError, [errorKey]: "" });
      } else if(value) {
        setFormError({
          ...formError,
          [errorKey]: "Timeout Seconds must be less than or equal to 120",
        });
      }
    } else if (name == "name") {
      const regex = /^[a-zA-Z0-9_-]{1,64}$/;
      if (regex.test(value)) {
        setFormError({ ...formError, [errorKey]: "" });
      } else {
        setFormError({
          ...formError,
          [errorKey]:
            "Name must only contain letters, numbers, underscores, or hyphens and be between 1-64 characters",
        });
      }
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const addHeaderItemData = () => {
    setFormData((prev) => ({
      ...prev,
      headers: [
        ...prev.headers,
        {
          id: uuidGenerator(),
          key: "",
          type: "",
          value: "",
        },
      ],
    }));
  };

  const handleChangeHeaderData = (e, id) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers?.map((header) => {
        if (header.id === id) {
          return { ...header, [name]: value };
        } else {
          return header;
        }
      }),
    }));
  };

  const deleteHeaderItemData = (id) => {
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers?.filter((header) => header.id != id),
    }));
  };

  const addSchemaItemData = () => {
    const newUUID = uuidGenerator();
    if (formData.schema.length == 0) {
      setCurrentSchema(newUUID);
    }
    setFormData((prev) => ({
      ...prev,
      schema: [
        ...prev.schema,
        {
          id: newUUID,
          name: "",
          description: "",
          type: "",
        },
      ],
    }));
  };

  const handleChangeSchemaData = (id, itemData) => {
    setFormData((prev) => ({
      ...prev,
      schema: prev.schema?.map((schemaItem) => {
        if (schemaItem.id === id) {
          return itemData;
        } else {
          return schemaItem;
        }
      }),
    }));
    setCurrentSchema(null);
  };

  const deleteSchemaItemData = (id) => {
    setFormData((prev) => ({
      ...prev,
      schema: prev.schema?.filter((schemaItem) => schemaItem.id != id),
    }));
  };

  const handleSubmit = async () => {
    let headers = {};
    let schema = {};
    let messages = [];
    formData.headers?.forEach((headerItem) => {
      headers[headerItem.key] = headerItem.value;
    });
    formData.schema?.forEach((schemaItem) => {
      schema[schemaItem.name] = {
        type: schemaItem.type,
        description: schemaItem.description,
      };
    });
    if (formData.startMessage) {
      messages.push({
        type: "request-start",
        content: formData.startMessage,
      });
    }
    if (formData.delayedResponseMessage) {
      messages.push({
        type: "request-response-delayed",
        content: formData.delayedResponseMessage,
      });
    }
    if (formData.successMessage) {
      messages.push({
        type: "request-complete",
        content: formData.successMessage,
        role: "assistant",
      });
    }
    if (formData.errorMessage) {
      messages.push({
        type: "request-failed",
        content: formData.errorMessage,
      });
    }
    let body = {
      messages: messages?.length ? messages : undefined,
      server: {
        url: formData.serverUrl,
        secret:
          formData.serverUrlToken && formData.serverUrlToken?.length
            ? formData.serverUrlToken
            : undefined,
        timeoutSeconds: parseInt(formData.timeoutSeconds) ?? 60,
        headers: formData.headers?.length ? headers : undefined,
      },
      function: {
        description:
          formData.description && formData.description?.length
            ? formData.description
            : undefined,
        async: false,
        name: formData.name,
        parameters: formData.schema?.length
          ? {
              type: "object",
              properties: schema,
            }
          : undefined,
      },
      type: "function",
      async: false,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(addFunction(body));
      if (
        addFunction.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        onSuccess("Function added Successfully!");
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevious = async () => {
    setStep((step) => (step > 1 ? step - 1 : step));
  };

  const handleNext = async () => {
    if (step == 4) {
      handleSubmit();
      return;
    }
    setStep((step) => (step < 4 ? step + 1 : step));
  };

  const hasFormError = useMemo(() => {
    return Object.values(formError).some((error) => error);
  }, [formError]);

  const isDisabled = useMemo(() => {
    if (step == 1) {
      const isHaveNotFilledHeaders = formData.headers?.length
        ? formData.headers.some(
            (header) => !header.key || !header.type || !header.value
          )
        : false;
      return (
        !formData.serverUrl ||
        !formData.timeoutSeconds ||
        isHaveNotFilledHeaders
      );
    } else if (step == 2) {
      const isHaveNotFilledSchemas = formData?.schema?.length
        ? formData.schema.some((schema) => !schema.name || !schema.type)
        : false;
      return isHaveNotFilledSchemas;
    } else if (step == 3) {
      return !formData.name;
    }
    return false;
  }, [formData, step]);

  const CurrentEditableSchema = ({ schemaItem }) => {
    const [currentSchemaData, setCurrentSchemaData] = useState(schemaItem);

    const handleChangeSchemaItem = (e) => {
      const { name, value } = e?.target;

      setCurrentSchemaData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSaveSchemaItem = () => {
      handleChangeSchemaData(schemaItem?.id, currentSchemaData);
    };

    return (
      <div
        className="formCol"
        style={{
          display: "grid",
          border: "1px solid #CBCAD7",
          padding: "13px 14px",
          borderRadius: "6px",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <div className="form__Field form__Field--full">
          <input
            type="text"
            id="name"
            name="name"
            value={currentSchemaData?.name}
            onChange={handleChangeSchemaItem}
            className="form__input form__input--sm"
            placeholder="Name"
            autoComplete="off"
          />
        </div>

        <div className="form__Field form__Field--full">
          <input
            type="text"
            id="description"
            name="description"
            value={currentSchemaData?.description}
            onChange={handleChangeSchemaItem}
            className="form__input form__input--sm"
            placeholder="Description"
            autoComplete="off"
          />
        </div>

        <div
          className="formRow formRow__2"
          style={{
            borderTop: "1px solid #CBCAD7",
            alignItems: "center",
            paddingTop: 8,
          }}
        >
          <select
            className="form__select form__select--sm"
            id="type"
            name="type"
            value={currentSchemaData?.type}
            style={{ maxWidth: "160px" }}
            onChange={handleChangeSchemaItem}
          >
            <option selected="" value="" disabled>
              -Select-
            </option>
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="object">Object</option>
            <option value="array">Array</option>
          </select>
          <div className="btn__group btn__group--end">
            <button
              className="btn btn__white"
              onClick={() => deleteSchemaItemData(schemaItem?.id)}
            >
              Delete
            </button>
            <button
              className="btn btn__primary"
              disabled={!currentSchemaData?.name || !currentSchemaData?.type}
              onClick={handleSaveSchemaItem}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  if (errorModal.visible) {
    return <ErrorModel message={errorModal.message} />;
  }

  return (
    <div className="modal modal--planSummery modal--open">
      <div className="modal__dialog">
        <div className="modal__content modal__content--md">
          <div className="modal__header">
            <h1 className="modal__header--title">Create a function</h1>
            <button
              className="btn btn__transparent btnClose"
              onClick={closePopup}
            >
              <CloseIcon />
            </button>
          </div>
          {step == 1 ? (
            <div className="modal__body  modal-body-left">
              <div className="createFormGrid">
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <h1
                        className="title title--h4"
                        style={{ marginBottom: "8px" }}
                      >
                        Custom function
                      </h1>
                      <p className="para">
                        Create a custom function or choose from one of the
                        supported providers.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="serverUrl"
                        className="form__label form__label--flex"
                      >
                        <span>
                          Server url
                          <sup className="form__label__asterisk">*</sup>
                        </span>
                      </label>
                      <input
                        type="text"
                        id="serverUrl"
                        name="serverUrl"
                        value={formData.serverUrl}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Server url"
                        autoComplete="off"
                      />
                      {formError?.serverUrlError ? (
                        <div className="form__error">
                          {formError?.serverUrlError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="serverUrlToken"
                        className="form__label form__label--flex"
                      >
                        Server url secret token
                      </label>
                      <input
                        type="password"
                        id="serverUrlToken"
                        name="serverUrlToken"
                        value={formData.serverUrlToken}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Server url secret token"
                        autoComplete="off"
                      />
                      {formError?.serverUrlTokenError ? (
                        <div className="form__error">
                          {formError?.serverUrlTokenError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="timeoutSeconds"
                        className="form__label form__label--flex"
                      >
                        <span>
                          Timeout seconds
                          <sup className="form__label__asterisk">*</sup>
                        </span>
                      </label>
                      <input
                        type="text"
                        id="timeoutSeconds"
                        name="timeoutSeconds"
                        value={formData.timeoutSeconds}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Timeout seconds"
                        maxLength={3}
                        autoComplete="off"
                      />
                      {formError?.timeoutSecondsError ? (
                        <div className="form__error">
                          {formError?.timeoutSecondsError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <h5
                        for="timeoutSeconds"
                        className="title title--h5"
                        style={{ marginBottom: "8px" }}
                      >
                        Headers
                      </h5>
                      {formData.headers.map((headerItemData, index) => (
                        <div className="formRow formRow__3">
                          <div className="formCol">
                            <div className="form__Field form__Field--full">
                              <label for="key" className="form__label">
                                Key
                              </label>
                              <input
                                type="text"
                                id="key"
                                name="key"
                                value={headerItemData.key}
                                onChange={(e) =>
                                  handleChangeHeaderData(e, headerItemData.id)
                                }
                                className="form__input form__input--sm"
                                placeholder="Key"
                                autoComplete="off"
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field form__Field--full">
                              <label for="type" className="form__label">
                                Type
                              </label>
                              <select
                                className="form__select form__select--sm"
                                id="type"
                                name="type"
                                value={headerItemData.type}
                                onChange={(e) =>
                                  handleChangeHeaderData(e, headerItemData.id)
                                }
                              >
                                <option selected="" value="" disabled>
                                  -Select-
                                </option>
                                <option value="string">String</option>
                                <option value="number">Number</option>
                                <option value="boolean">Boolean</option>
                                <option value="object">Object</option>
                              </select>
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field form__Field--full">
                              <label for="value" className="form__label">
                                Value
                              </label>
                              <div className="form__group__flex">
                                <input
                                  type="text"
                                  id="value"
                                  name="value"
                                  value={headerItemData.value}
                                  onChange={(e) =>
                                    handleChangeHeaderData(e, headerItemData.id)
                                  }
                                  className="form__input form__input--sm"
                                  placeholder="Value"
                                  autoComplete="off"
                                />
                                <button
                                  type="button"
                                  className="btn btn__danger btn__outlined"
                                  style={{ height: "44px" }}
                                  onClick={() =>
                                    deleteHeaderItemData(headerItemData.id)
                                  }
                                >
                                  <DeleteIcon />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <button
                    type="button"
                    className="btn btn__outlined"
                    onClick={addHeaderItemData}
                  >
                    Add Row
                  </button>
                </div>
              </div>
            </div>
          ) : step == 2 ? (
            <div className="modal__body  modal-body-left">
              <div className="createFormGrid">
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <h1
                        className="title title--h4"
                        style={{ marginBottom: "8px" }}
                      >
                        Build your schema
                      </h1>
                      <p className="para">
                        Define your schema which the LLM should generate.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <button
                    type="button"
                    className="btn btn__outlined"
                    onClick={addSchemaItemData}
                  >
                    <AddIcon color="#111" />
                    Properties
                  </button>
                </div>
                {formData.schema?.map((schemaItem) => (
                  <div className="createFormGrid__row">
                    {currentSchema == schemaItem.id ? (
                      <CurrentEditableSchema schemaItem={schemaItem} />
                    ) : (
                      <div className="formCol">
                        <div
                          className="formRow formRow__2"
                          style={{
                            border: "1px solid #CBCAD7",
                            padding: "13px 14px",
                            borderRadius: "6px",
                            alignItems: "center",
                          }}
                          onClick={() => setCurrentSchema(schemaItem.id)}
                        >
                          <h5 className="title title--h6">
                            {schemaItem?.name}
                          </h5>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end",
                            }}
                          >
                            <CollapseIcon />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : step == 3 ? (
            <div className="modal__body  modal-body-left">
              <div className="createFormGrid">
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <h1
                        className="title title--h4"
                        style={{ marginBottom: "8px" }}
                      >
                        What is this function for?
                      </h1>
                      <p className="para">
                        Add details to your function which will be used by the
                        LLM to understand and call the function.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="name"
                        className="form__label form__label--flex"
                      >
                        <span>
                          Name
                          <sup className="form__label__asterisk">*</sup>
                        </span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="What is your function name?"
                        autoComplete="off"
                      />
                      {formError?.nameError ? (
                        <div className="form__error">
                          {formError?.nameError}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="description"
                        className="form__label form__label--flex"
                      >
                        Description
                      </label>
                      <textarea
                        id="description"
                        className="form__textarea"
                        name="description"
                        maxLength={500}
                        value={formData.description}
                        onChange={handleChange}
                        placeholder="Describe the function, its purpose, its uses etc."
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : step == 4 ? (
            <div className="modal__body  modal-body-left">
              <div className="createFormGrid">
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <h1
                        className="title title--h4"
                        style={{ marginBottom: "8px" }}
                      >
                        What messages should be shown to the user as the tool is
                        running?
                      </h1>
                      <p className="para">
                        Configure the messages that should be shown to the user
                        as the tool is running.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="startMessage"
                        className="form__label form__label--flex"
                      >
                        Start message
                      </label>
                      <input
                        type="text"
                        id="startMessage"
                        name="startMessage"
                        value={formData.startMessage}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder='Message to show when the tool starts running (e.g. "Fetching user information...")'
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="delayedResponseMessage"
                        className="form__label form__label--flex"
                      >
                        Delayed response message
                      </label>
                      <input
                        type="text"
                        id="delayedResponseMessage"
                        name="delayedResponseMessage"
                        value={formData.delayedResponseMessage}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Message to show when the response is taking longer than expected"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="successMessage"
                        className="form__label form__label--flex"
                      >
                        Success message
                      </label>
                      <input
                        type="text"
                        id="successMessage"
                        name="successMessage"
                        value={formData.successMessage}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Message to show when the tool completes successfully"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
                <div className="createFormGrid__row">
                  <div className="formCol">
                    <div className="form__Field form__Field--full">
                      <label
                        for="errorMessage"
                        className="form__label form__label--flex"
                      >
                        Error message
                      </label>
                      <input
                        type="text"
                        id="errorMessage"
                        name="errorMessage"
                        value={formData.errorMessage}
                        onChange={handleChange}
                        className="form__input form__input--sm"
                        placeholder="Message to show if the tool execution fails"
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <div className="modal__footer">
            <div className="btn__group">
              <button className="btn btn__white" onClick={closePopup}>
                Cancel
              </button>
              {step != 1 ? (
                <button className="btn btn__primary" onClick={handlePrevious}>
                  Previous
                </button>
              ) : null}
              <button
                className="btn btn__primary"
                disabled={isDisabled || hasFormError}
                onClick={handleNext}
              >
                {step == 4 ? "Create" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal__backdrop"></div>
    </div>
  );
};

export default CreateFunctionModal;
